// Face-painting time/cost estimate for a given number of children. Pure (no
// server imports), so the same code renders the initial result on the server
// and recomputes it in the browser (src/frontend/face-painting-calculator.ts).

export interface HourlyTier {
    price: number;
    // 1-based hour numbers the price applies to, as in the Offer's eligibleQuantity
    // (e.g. hours 1–3, then hours 4+ with no maxQuantity).
    minQuantity?: number;
    maxQuantity?: number;
}

export interface FacePaintingEstimateConfig {
    childrenPerHour: { min: number; max: number };
    // Time is rounded up to this step (in hours); a started step is paid proportionally.
    roundingHours: number;
    // Shortest bookable time; fewer children are still priced for this many hours.
    minHours: number;
    tiers: HourlyTier[];
}

export interface FacePaintingEstimate {
    hours: { min: number; max: number };
    cost: { min: number; max: number };
}

export interface EstimateTexts {
    // Intl locale used to format numbers ("2,5" vs "2.5").
    numberLocale: string;
    // Templates with {{value}} for a single value, {{min}}/{{max}} for a range.
    hoursValue: string;
    hoursRange: string;
    costValue: string;
    costRange: string;
}

export const FACE_PAINTING_THROUGHPUT = { min: 8, max: 10 };
export const FACE_PAINTING_ROUNDING_HOURS = 0.5;
export const FACE_PAINTING_MIN_HOURS = 1;

function bookedHours(children: number, childrenPerHour: number, config: FacePaintingEstimateConfig): number {
    const rounded = Math.ceil(children / childrenPerHour / config.roundingHours - 1e-9) * config.roundingHours;
    return Math.max(config.minHours, rounded);
}

export function costForHours(hours: number, tiers: HourlyTier[]): number {
    let cost = 0;
    for (const tier of tiers) {
        const from = (tier.minQuantity ?? 1) - 1;
        const to = tier.maxQuantity ?? Number.POSITIVE_INFINITY;
        const hoursInTier = Math.max(0, Math.min(hours, to) - from);
        cost += hoursInTier * tier.price;
    }
    return Math.round(cost);
}

export function estimateFacePainting(children: number, config: FacePaintingEstimateConfig): FacePaintingEstimate {
    // Faster pace -> shorter time -> lower cost.
    const minHours = bookedHours(children, config.childrenPerHour.max, config);
    const maxHours = bookedHours(children, config.childrenPerHour.min, config);
    return {
        hours: { min: minHours, max: maxHours },
        cost: { min: costForHours(minHours, config.tiers), max: costForHours(maxHours, config.tiers) },
    };
}

function fill(template: string, values: Record<string, string>): string {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => values[key]);
}

function formatRange(
    range: { min: number; max: number },
    value: string,
    rangeTemplate: string,
    locale: string,
): string {
    const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    if (range.min === range.max) {
        return fill(value, { value: format.format(range.min) });
    }
    return fill(rangeTemplate, { min: format.format(range.min), max: format.format(range.max) });
}

export function formatEstimate(estimate: FacePaintingEstimate, texts: EstimateTexts): { hours: string; cost: string } {
    return {
        hours: formatRange(estimate.hours, texts.hoursValue, texts.hoursRange, texts.numberLocale),
        cost: formatRange(estimate.cost, texts.costValue, texts.costRange, texts.numberLocale),
    };
}

// Handed to the face-painting view: renders the initial result on the server,
// and its config goes to the browser script as JSON.
export class FacePaintingCalculator {
    constructor(readonly config: FacePaintingEstimateConfig & { defaultChildren: number }) {}

    format(children: number, texts: EstimateTexts): { hours: string; cost: string } {
        return formatEstimate(estimateFacePainting(children, this.config), texts);
    }
}
